import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, DateInput, LoadingState, Modal, Select, Textarea, useToast } from '@orbind/ui'
import { api } from '../lib/api.ts'
import { PlansSection } from '../features/admin/Plans.tsx'
import { FeatureTogglesSection } from '../features/admin/FeatureToggles.tsx'
import { PluginsDoConsoleSection } from '../features/console/PluginsDoConsole.tsx'
import { DesenvolvedoresSection } from '../features/console/Desenvolvedores.tsx'
import { ConsoleSettingsSection } from '../features/admin/ConsoleSettings.tsx'
import { FundosDeQuadroSection } from '../features/console/FundosDeQuadro.tsx'
import { GoogleCalendarConfigSection } from '../features/console/GoogleCalendarConfig.tsx'
import './admin.css'
import './console.css'

const SECOES = [
  { id: 'dashboard', label: 'Visão geral', icon: 'fas fa-gauge-high', meta: 'Contas, workspaces e itens' },
  { id: 'acessos', label: 'Acessos & uso', icon: 'fas fa-chart-line', meta: 'Diários, rank e funcionalidades' },
  { id: 'comunicacao', label: 'Comunicação', icon: 'fas fa-comments', meta: 'Uso do chat por período' },
  { id: 'solicitacoes', label: 'Solicitações', icon: 'fas fa-envelope-open-text', meta: 'Pedidos de convite' },
  { id: 'planos', label: 'Planos', icon: 'fas fa-cubes', meta: 'Pacotes de módulos por conta' },
  { id: 'feature-toggles', label: 'Feature toggles', icon: 'fas fa-toggle-on', meta: 'Módulos e exceções' },
  { id: 'plugins', label: 'Plugins', icon: 'fas fa-plug', meta: 'Cadastro pelo manifesto' },
  { id: 'desenvolvedores', label: 'Desenvolvedores', icon: 'fas fa-user-astronaut', meta: 'Quem publica plugin' },
  // biblioteca global de fundos (SPEC-20261027-1840-biblioteca-de-fundos-e-imagem-do-quadro)
  { id: 'fundos', label: 'Fundos de quadro', icon: 'far fa-image', meta: 'Biblioteca global de imagens' },
  { id: 'configuracoes', label: 'Configurações', icon: 'fas fa-sliders-h', meta: 'Comportamento da instalação' },
  // passo a passo da integração (SPEC-20261027-2350-integracao-google-calendar)
  { id: 'google-calendar', label: 'Google Calendar', icon: 'fab fa-google', meta: 'Ligar a agenda do Google' },
] as const

/** Console do super admin: espelha o layout da Administração (explorer + main). */
export function ConsolePage() {
  const navigate = useNavigate()
  const { section: sectionId } = useParams()
  const section = SECOES.find((s) => s.id === sectionId) ?? SECOES[0]

  return (
    <div className="cons-shell">
      <nav className="cons-nav" aria-label="Console">
        <div className="cons-explorer-title">
          <i className="fas fa-terminal" aria-hidden /> Console
        </div>
        <div className="adm-seclist">
          {SECOES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`adm-secrow${item.id === section.id ? ' adm-secrow--on' : ''}`}
              aria-current={item.id === section.id ? 'page' : undefined}
              onClick={() => navigate(`/console/${item.id}`)}
            >
              <span className="adm-secrow__icon">
                <i className={item.icon} aria-hidden />
              </span>
              <span className="adm-secrow__main">
                <span className="adm-secrow__label">{item.label}</span>
                <span className="adm-secrow__meta">{item.meta}</span>
              </span>
              <i className="fas fa-chevron-right adm-secrow__chevron" aria-hidden />
            </button>
          ))}
        </div>
      </nav>
      <div className="cons-main adm-page">
        {section.id === 'acessos' ? (
          <ConsoleAcessos />
        ) : section.id === 'comunicacao' ? (
          <ConsoleComunicacao />
        ) : section.id === 'solicitacoes' ? (
          <ConsoleSolicitacoes />
        ) : section.id === 'planos' ? (
          <PlansSection />
        ) : section.id === 'feature-toggles' ? (
          <FeatureTogglesSection />
        ) : section.id === 'plugins' ? (
          <PluginsDoConsoleSection />
        ) : section.id === 'desenvolvedores' ? (
          <DesenvolvedoresSection />
        ) : section.id === 'fundos' ? (
          <FundosDeQuadroSection />
        ) : section.id === 'google-calendar' ? (
          <GoogleCalendarConfigSection />
        ) : section.id === 'configuracoes' ? (
          <ConsoleSettingsSection />
        ) : (
          <ConsoleDashboard />
        )}
      </div>
    </div>
  )
}

const nf = new Intl.NumberFormat('pt-BR')

function Metric({ icon, valor, label, sub }: { icon: string; valor: number; label: string; sub?: string }) {
  return (
    <div className="cons-card">
      <span className="cons-card__icon">
        <i className={icon} aria-hidden />
      </span>
      <span className="cons-card__valor">{nf.format(valor)}</span>
      <span className="cons-card__label">{label}</span>
      {sub ? <span className="cons-card__sub">{sub}</span> : null}
    </div>
  )
}

// ------------------------------------------------------------ Visão geral ---

function ConsoleDashboard() {
  const [recolhidas, setRecolhidas] = useState<Set<string>>(new Set())
  const q = useQuery({
    queryKey: ['console', 'overview'],
    queryFn: async () => (await api.console.overview.$get()).json(),
  })

  if (q.isPending) return <LoadingState variant="detail" label="Carregando Console" />
  if (q.isError || !q.data)
    return (
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Console</h1>
          <p className="adm-head__desc">Não foi possível carregar os dados. Tente novamente.</p>
        </div>
      </div>
    )

  const d = q.data
  const chaveConta = (c: (typeof d.arvore)[number]) => c.accountId
  const rotuloTipo = (k: string) => (k === 'personal' ? 'Pessoa física' : 'Empresa')
  const toggle = (chave: string) =>
    setRecolhidas((atual) => {
      const prox = new Set(atual)
      if (prox.has(chave)) prox.delete(chave)
      else prox.add(chave)
      return prox
    })

  return (
    <>
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Visão geral</h1>
          <p className="adm-head__desc">Números da instalação do Orbind — contas, membros, workspaces e itens.</p>
        </div>
      </div>

      <div className="cons-cards">
        <Metric icon="fas fa-id-card" valor={d.contas} label="Contas" sub="donos que criaram uma conta" />
        <Metric icon="fas fa-users" valor={d.membros} label="Membros" sub="pessoas na instalação" />
        <Metric icon="fas fa-layer-group" valor={d.workspaces} label="Workspaces" sub="em toda a instalação" />
        <Metric icon="fas fa-diagram-project" valor={d.itens} label="Itens de Trabalho" sub="em toda a instalação" />
      </div>

      <div className="cons-panel">
        <div className="cons-panel__head">
          <h2 className="cons-panel__title">Por conta</h2>
          <span className="cons-panel__meta">{nf.format(d.arvore.length)} contas</span>
        </div>
        {d.arvore.length === 0 ? (
          <p className="adm-hint">Nenhuma conta ainda.</p>
        ) : (
          <div className="cons-tree">
            <div className="cons-tree__head">
              <span>Conta / Workspace</span>
              <span>Plano</span>
              <span className="cons-tree__num">Membros</span>
              <span className="cons-tree__num">Itens</span>
            </div>
            {d.arvore.map((conta) => {
              const chave = chaveConta(conta)
              const aberta = !recolhidas.has(chave)
              return (
                <div className="cons-tree__conta" key={chave}>
                  <button type="button" className="cons-tree__row cons-tree__row--conta" onClick={() => toggle(chave)}>
                    <span className="cons-tree__nome">
                      <i className={`fas fa-chevron-${aberta ? 'down' : 'right'} cons-tree__chev`} aria-hidden />
                      <i className="fas fa-id-card cons-tree__ico" aria-hidden />
                      <span className="cons-tree__label">
                        <strong>{conta.ownerNome}</strong>
                        {conta.ownerEmail ? <span className="cons-tree__sub">{conta.ownerEmail}</span> : null}
                      </span>
                      <span className="cons-tree__badge" title="Tipo da conta">{rotuloTipo(conta.kind)}</span>
                      <span className="cons-tree__badge">{conta.workspaces.length} ws</span>
                    </span>
                    <span>{conta.plano ? <span className="cons-plan">{conta.plano}</span> : <span className="cons-plan cons-plan--none">sem plano</span>}</span>
                    <span className="cons-tree__num">{nf.format(conta.membros)}</span>
                    <span className="cons-tree__num">{nf.format(conta.itens)}</span>
                  </button>
                  {aberta
                    ? conta.workspaces.map((w) => (
                        <div className="cons-tree__row cons-tree__row--ws" key={w.id}>
                          <span className="cons-tree__nome">
                            <span className={`cons-dot${w.isActive ? ' cons-dot--on' : ''}`} title={w.isActive ? 'Ativo' : 'Inativo'} />
                            <i className="fas fa-layer-group cons-tree__ico cons-tree__ico--ws" aria-hidden />
                            {w.name}
                          </span>
                          <span className="cons-tree__herda" title="O plano é da conta">↳ herda</span>
                          <span className="cons-tree__num">{nf.format(w.membros)}</span>
                          <span className="cons-tree__num">{nf.format(w.itens)}</span>
                        </div>
                      ))
                    : null}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

// ------------------------------------------------------------- Acessos ------

type Preset = 'hoje' | '7' | '15' | '30' | '60' | '90' | 'periodo'
const PRESETS: { id: Preset; label: string }[] = [
  { id: 'hoje', label: 'Hoje' },
  { id: '7', label: '7 dias' },
  { id: '15', label: '15 dias' },
  { id: '30', label: '30 dias' },
  { id: '60', label: '60 dias' },
  { id: '90', label: '90 dias' },
  { id: 'periodo', label: 'Período' },
]

const startOfDay = (d: Date) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}
const endOfDay = (d: Date) => {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}
function rangeDoPreset(preset: Preset, ini: Date | null, fim: Date | null): { from: string; to: string } {
  const agora = new Date()
  if (preset === 'periodo') {
    if (!ini || !fim) return { from: '', to: '' }
    return { from: startOfDay(ini).toISOString(), to: endOfDay(fim).toISOString() }
  }
  if (preset === 'hoje') return { from: startOfDay(agora).toISOString(), to: agora.toISOString() }
  const dias = Number(preset)
  return { from: startOfDay(new Date(agora.getTime() - (dias - 1) * 86_400_000)).toISOString(), to: agora.toISOString() }
}

const iniciais = (nome: string) =>
  nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '—'

const diaCurto = (iso: string) => {
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}

function ConsoleAcessos() {
  const [preset, setPreset] = useState<Preset>('30')
  const [ini, setIni] = useState<Date | null>(null)
  const [fim, setFim] = useState<Date | null>(null)
  const { from, to } = useMemo(() => rangeDoPreset(preset, ini, fim), [preset, ini, fim])

  const q = useQuery({
    queryKey: ['console', 'access', from, to],
    queryFn: async () => (await api.console.access.$get({ query: { from, to } })).json(),
    enabled: from !== '' && to !== '',
  })

  const dados = q.data
  const maxDia = Math.max(1, ...(dados?.acessosDiarios ?? []).map((d) => d.total))
  const maxRank = Math.max(1, ...(dados?.rankMembros ?? []).map((r) => r.total))
  const maxFeat = Math.max(1, ...(dados?.funcionalidades ?? []).map((f) => f.total))

  return (
    <>
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Acessos &amp; uso</h1>
          <p className="adm-head__desc">Acessos diários, quem mais entra e quais funcionalidades são mais usadas.</p>
        </div>
      </div>

      <div className="cons-presets" role="tablist" aria-label="Período">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`cons-preset${preset === p.id ? ' cons-preset--on' : ''}`}
            aria-selected={preset === p.id}
            onClick={() => setPreset(p.id)}
          >
            {p.label}
          </button>
        ))}
        {preset === 'periodo' ? (
          <span className="cons-periodo">
            <DateInput value={ini} onChange={setIni} placeholder="Início" />
            <span className="cons-periodo__sep">até</span>
            <DateInput value={fim} onChange={setFim} placeholder="Fim" />
          </span>
        ) : null}
      </div>

      {preset === 'periodo' && (from === '' || to === '') ? (
        <p className="adm-hint">Escolha a data inicial e a final para ver os acessos do período.</p>
      ) : q.isPending ? (
        <LoadingState variant="detail" label="Carregando acessos" />
      ) : q.isError || !dados ? (
        <p className="adm-hint">Não foi possível carregar os acessos.</p>
      ) : (
        <>
          <div className="cons-panel">
            <div className="cons-panel__head">
              <h2 className="cons-panel__title">Acessos por dia</h2>
              <span className="cons-panel__meta">{nf.format(dados.totalAcessos)} acessos no período</span>
            </div>
            {dados.acessosDiarios.length === 0 ? (
              <p className="adm-hint">Nenhum acesso registrado neste período.</p>
            ) : (
              <div className="cons-daybars">
                {dados.acessosDiarios.map((d) => (
                  <span className="cons-daybar" key={d.dia} title={`${diaCurto(d.dia)} · ${nf.format(d.total)} acessos`}>
                    <span className="cons-daybar__fill" style={{ height: `${(d.total / maxDia) * 100}%` }}>
                      <span className="cons-daybar__val">{d.total}</span>
                    </span>
                    <span className="cons-daybar__lbl">{diaCurto(d.dia)}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="cons-cols">
            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Top 10 — quem mais acessa</h2>
              </div>
              {dados.rankMembros.length === 0 ? (
                <p className="adm-hint">Sem acessos no período.</p>
              ) : (
                <ol className="cons-rank">
                  {dados.rankMembros.map((r, i) => (
                    <li className="cons-rank__row" key={r.memberId}>
                      <span className="cons-rank__pos">{i + 1}</span>
                      <span className="cons-rank__av">{iniciais(r.name)}</span>
                      <span className="cons-rank__main">
                        <span className="cons-rank__name">{r.name}</span>
                        <span className="cons-bar">
                          <span className="cons-bar__fill" style={{ width: `${(r.total / maxRank) * 100}%` }} />
                        </span>
                      </span>
                      <span className="cons-rank__val">{nf.format(r.total)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Funcionalidades mais acessadas</h2>
              </div>
              {dados.funcionalidades.length === 0 ? (
                <p className="adm-hint">Nenhuma navegação registrada ainda neste período.</p>
              ) : (
                <ul className="cons-feat">
                  {dados.funcionalidades.map((f) => (
                    <li className="cons-feat__row" key={f.feature}>
                      <span className="cons-feat__name">{f.feature}</span>
                      <span className="cons-bar">
                        <span className="cons-bar__fill cons-bar__fill--alt" style={{ width: `${(f.total / maxFeat) * 100}%` }} />
                      </span>
                      <span className="cons-feat__val">{nf.format(f.total)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------- Solicitações ---

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendente',
  UNDER_REVIEW: 'Em análise',
  APPROVED: 'Aprovada',
  INVITED: 'Convite enviado',
  WAITLIST: 'Lista de espera',
  REJECTED: 'Não aprovada',
  CANCELLED: 'Cancelada',
}
const TEAM_SIZE_LABEL: Record<string, string> = {
  '1': 'Somente eu',
  '2_TO_5': '2 a 5',
  '6_TO_10': '6 a 10',
  '11_TO_25': '11 a 25',
  '26_TO_50': '26 a 50',
  '51_TO_100': '51 a 100',
  '101_TO_250': '101 a 250',
  '251_PLUS': 'Mais de 250',
}
const AREA_LABEL: Record<string, string> = {
  OPERATIONS: 'Operação',
  PRODUCT: 'Produto',
  DEVELOPMENT: 'Desenvolvimento',
  MARKETING: 'Marketing',
  SALES: 'Vendas',
  CUSTOMER_SUCCESS: 'Sucesso do Cliente',
  ADMINISTRATION: 'Administrativo',
  OTHER: 'Outra',
}
const INTEREST_LABEL: Record<string, string> = {
  DEMANDS: 'Demandas',
  WORK_ITEMS: 'Projetos e Itens de Trabalho',
  SOFTWARE_DEVELOPMENT: 'Desenvolvimento de software',
  OPERATIONS: 'Operação',
  MARKETING: 'Marketing',
  TEAM_COMMUNICATION: 'Comunicação entre equipes',
  AUTOMATIONS: 'Automações',
  ALL_IN_ONE: 'Tudo em um único ambiente',
}
const rotulo = (mapa: Record<string, string>, k: string) => mapa[k] ?? k

function ConsoleSolicitacoes() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const q = useQuery({
    queryKey: ['console', 'access-requests'],
    queryFn: async () => (await api.console['access-requests'].$get()).json(),
  })

  // os planos ativos: o plano é decisão de quem libera (2026-10-01)
  const planos = useQuery({
    queryKey: ['onboarding', 'plans'],
    queryFn: async () => (await api.onboarding.plans.$get()).json(),
  })

  /** Quem está sendo liberado ou recusado — os dois pedem uma decisão a mais. */
  const [liberando, setLiberando] = useState<{ id: string; nome: string } | null>(null)
  const [recusando, setRecusando] = useState<{ id: string; nome: string } | null>(null)
  const [planoId, setPlanoId] = useState<string>('')
  const [motivo, setMotivo] = useState('')
  /** O link volta na resposta: se o e-mail falhar, ainda dá para repassar. */
  const [linkGerado, setLinkGerado] = useState<{ link: string; emailEnviado: boolean } | null>(null)

  const liberar = useMutation({
    mutationFn: async (entrada: { id: string; planId: string | null }) =>
      (
        await api.console['access-requests'][':id'].approve.$post({
          param: { id: entrada.id },
          json: { planId: entrada.planId },
        })
      ).json(),
    onSuccess: (r) => {
      setLiberando(null)
      setLinkGerado({ link: r.link, emailEnviado: r.emailEnviado })
      void queryClient.invalidateQueries({ queryKey: ['console', 'access-requests'] })
      toast.push({
        tone: r.emailEnviado ? 'success' : 'warning',
        message: r.emailEnviado
          ? 'Acesso liberado e convite enviado por e-mail.'
          : 'Acesso liberado, mas o e-mail não saiu. Copie o link e envie você mesmo.',
      })
    },
    onError: (e) =>
      toast.push({
        tone: 'danger',
        message: e instanceof Error ? e.message : 'Não foi possível liberar.',
      }),
  })

  const recusar = useMutation({
    mutationFn: async (entrada: { id: string; motivo: string }) =>
      (
        await api.console['access-requests'][':id'].reject.$post({
          param: { id: entrada.id },
          json: { motivo: entrada.motivo },
        })
      ).json(),
    onSuccess: () => {
      setRecusando(null)
      setMotivo('')
      void queryClient.invalidateQueries({ queryKey: ['console', 'access-requests'] })
      toast.push({ tone: 'success', message: 'Solicitação recusada.' })
    },
    onError: (e) =>
      toast.push({
        tone: 'danger',
        message: e instanceof Error ? e.message : 'Não foi possível recusar.',
      }),
  })

  if (q.isPending) return <LoadingState variant="detail" label="Carregando solicitações" />
  if (q.isError || !q.data)
    return (
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Solicitações</h1>
          <p className="adm-head__desc">Não foi possível carregar as solicitações.</p>
        </div>
      </div>
    )

  const reqs = q.data.requests
  return (
    <>
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Solicitações de convite</h1>
          <p className="adm-head__desc">Pedidos de acesso recebidos pelo formulário público, para análise futura.</p>
        </div>
      </div>

      {reqs.length === 0 ? (
        <div className="cons-panel">
          <p className="adm-hint">Nenhuma solicitação recebida ainda.</p>
        </div>
      ) : (
        <div className="cons-reqs">
          {reqs.map((r) => (
            <div className="cons-req" key={r.id}>
              <div className="cons-req__head">
                <span className="cons-req__nome">
                  <strong>{r.fullName}</strong>
                  <span className="cons-req__email">{r.email}</span>
                </span>
                <span className={`cons-req__status cons-req__status--${r.status.toLowerCase()}`}>
                  {rotulo(STATUS_LABEL, r.status)}
                </span>
              </div>
              <div className="cons-req__meta">
                <span>
                  <i className="fas fa-building" aria-hidden /> {r.companyName}
                </span>
                <span>
                  <i className="fas fa-users" aria-hidden /> {rotulo(TEAM_SIZE_LABEL, r.teamSize)}
                </span>
                <span>
                  <i className="fas fa-sitemap" aria-hidden /> {rotulo(AREA_LABEL, r.primaryArea)}
                </span>
                <span>
                  <i className="far fa-clock" aria-hidden />{' '}
                  {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
                    new Date(r.createdAt),
                  )}
                </span>
              </div>
              {r.interests.length > 0 ? (
                <div className="cons-req__tags">
                  {r.interests.map((i) => (
                    <span className="cons-req__tag" key={i}>
                      {rotulo(INTEREST_LABEL, i)}
                    </span>
                  ))}
                </div>
              ) : null}
              {r.mainChallenge ? <p className="cons-req__desafio">“{r.mainChallenge}”</p> : null}

              {/* QUEM decidiu e QUANDO — era o que faltava para a tela dizer
                  algo além de "pendente" (pedido do Cristian, 2026-10-01). */}
              {r.reviewedAt ? (
                <p className="cons-req__decisao">
                  <i className="fas fa-user-check" aria-hidden />{' '}
                  {r.status === 'REJECTED' ? 'Recusada' : 'Liberada'} em{' '}
                  {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
                    new Date(r.reviewedAt),
                  )}
                  {r.acceptedAt ? ' · a pessoa já entrou' : ''}
                </p>
              ) : null}
              {r.reviewReason ? <p className="cons-req__motivo">{r.reviewReason}</p> : null}

              {/* só decide quem ainda não entrou: depois disso não há o que decidir */}
              {r.acceptedAt === null && r.status !== 'REJECTED' ? (
                <div className="cons-req__acoes">
                  <Button
                    variant="primary"
                    size="sm"
                    icon="fas fa-paper-plane"
                    onClick={() => {
                      setPlanoId('')
                      setLinkGerado(null)
                      setLiberando({ id: r.id, nome: r.fullName })
                    }}
                  >
                    {r.status === 'INVITED' ? 'Reenviar convite' : 'Liberar acesso'}
                  </Button>
                  <Button
                    size="sm"
                    icon="fas fa-times"
                    onClick={() => {
                      setMotivo('')
                      setRecusando({ id: r.id, nome: r.fullName })
                    }}
                  >
                    Recusar
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <Modal
        open={liberando !== null}
        onClose={() => setLiberando(null)}
        title={liberando ? `Liberar acesso de ${liberando.nome}` : ''}
        width={480}
        footer={
          <>
            <Button variant="ghost" onClick={() => setLiberando(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              icon="fas fa-paper-plane"
              disabled={liberar.isPending}
              onClick={() => liberando && liberar.mutate({ id: liberando.id, planId: planoId || null })}
            >
              {liberar.isPending ? 'Liberando…' : 'Liberar e enviar convite'}
            </Button>
          </>
        }
      >
        <div className="cons-lib">
          <p className="cons-lib__texto">
            A pessoa recebe um link por e-mail, cria a própria senha e dá nome ao espaço da equipe. O convite vale
            por 7 dias e só pode ser usado uma vez.
          </p>
          <label className="cons-lib__campo">
            <span className="cons-lib__rotulo">Plano</span>
            {/* o plano é decisão de quem libera; o nome do workspace é de quem entra */}
            <Select
              value={planoId}
              placeholder="Sem plano definido"
              options={(planos.data?.plans ?? []).map((p) => ({ value: p.id, label: p.name }))}
              onChange={(v) => setPlanoId(v)}
            />
            <span className="cons-lib__dica">Pode ser trocado depois, na tela de Planos.</span>
          </label>
        </div>
      </Modal>

      <Modal
        open={linkGerado !== null}
        onClose={() => setLinkGerado(null)}
        title="Convite gerado"
        width={520}
        footer={
          <Button variant="primary" onClick={() => setLinkGerado(null)}>
            Fechar
          </Button>
        }
      >
        <div className="cons-lib">
          <p className="cons-lib__texto">
            {linkGerado?.emailEnviado
              ? 'O convite foi enviado por e-mail. Se precisar, este é o mesmo link:'
              : 'O e-mail não saiu. Copie o link abaixo e envie você mesmo — sem ele, a pessoa não consegue entrar:'}
          </p>
          <code className="cons-lib__link">{linkGerado?.link}</code>
          <Button
            size="sm"
            icon="fas fa-copy"
            onClick={() => {
              void navigator.clipboard.writeText(linkGerado?.link ?? '')
              toast.push({ tone: 'success', message: 'Link copiado.' })
            }}
          >
            Copiar link
          </Button>
        </div>
      </Modal>

      <Modal
        open={recusando !== null}
        onClose={() => setRecusando(null)}
        title={recusando ? `Recusar ${recusando.nome}` : ''}
        width={480}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRecusando(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={recusar.isPending || motivo.trim() === ''}
              onClick={() => recusando && recusar.mutate({ id: recusando.id, motivo: motivo.trim() })}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <div className="cons-lib__campo">
          <span className="cons-lib__rotulo">Por quê? *</span>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={4} />
          <span className="cons-lib__dica">
            Fica registrado com a decisão. Não é enviado automaticamente à pessoa.
          </span>
        </div>
      </Modal>
    </>
  )
}

// ----------------------------------------------------------- Comunicação ---

/** Rótulos dos tipos de canal (`TIPOS_DE_CANAL` do core) — `pessoa` é a direta. */
const TIPO_CANAL_LABEL: Record<string, string> = {
  time: 'Time',
  produto: 'Produto',
  empresa: 'Empresa',
  projeto: 'Projeto',
  pessoa: 'Conversa direta',
}
const TIPO_CANAL_ICON: Record<string, string> = {
  time: 'fas fa-users',
  produto: 'fas fa-cube',
  empresa: 'fas fa-building',
  projeto: 'fas fa-diagram-project',
  pessoa: 'fas fa-user',
}
const ANEXO_LABEL: Record<string, string> = {
  documento: 'Documentos',
  imagem: 'Imagens',
  video: 'Vídeos',
  audio: 'Áudios',
  contato: 'Contatos',
  enquete: 'Enquetes',
  evento: 'Eventos',
}
const DIA_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']

/** "1h 20min", "35 min", "—" */
const minutosPorExtenso = (min: number | null) => {
  if (min == null) return '—'
  if (min < 1) return 'menos de 1 min'
  if (min < 60) return `${Math.round(min)} min`
  const h = Math.floor(min / 60)
  const m = Math.round(min % 60)
  return m ? `${h}h ${m}min` : `${h}h`
}

/**
 * Uso do chat por período (SPEC-20260915-2000). Mesmos presets e a mesma
 * gramática visual de "Acessos & uso": cartões, barras por dia, rankings.
 * Só números — a rota nunca devolve texto de mensagem.
 */
function ConsoleComunicacao() {
  const [preset, setPreset] = useState<Preset>('30')
  const [ini, setIni] = useState<Date | null>(null)
  const [fim, setFim] = useState<Date | null>(null)
  const { from, to } = useMemo(() => rangeDoPreset(preset, ini, fim), [preset, ini, fim])

  const q = useQuery({
    queryKey: ['console', 'chat', from, to],
    queryFn: async () => (await api.console.chat.$get({ query: { from, to } })).json(),
    enabled: from !== '' && to !== '',
  })

  const d = q.data
  const maxDia = Math.max(1, ...(d?.porDia ?? []).map((x) => x.mensagens))
  const maxHora = Math.max(1, ...(d?.porHora ?? []).map((x) => x.total))
  const maxSemana = Math.max(1, ...(d?.porDiaSemana ?? []).map((x) => x.total))
  const maxCanal = Math.max(1, ...(d?.rankCanais ?? []).map((x) => x.mensagens))
  const maxPessoa = Math.max(1, ...(d?.rankPessoas ?? []).map((x) => x.mensagens))
  const maxTipo = Math.max(1, ...(d?.porTipo ?? []).map((x) => x.mensagens))
  // as 24 horas sempre aparecem, mesmo as sem mensagem — o vazio também informa
  const horas = Array.from({ length: 24 }, (_, h) => ({ hora: h, total: d?.porHora.find((x) => x.hora === h)?.total ?? 0 }))
  const semana = Array.from({ length: 7 }, (_, i) => ({ dia: i + 1, total: d?.porDiaSemana.find((x) => x.dia === i + 1)?.total ?? 0 }))
  const vazio = !!d && d.totais.mensagens === 0

  return (
    <>
      <div className="adm-head">
        <div>
          <h1 className="adm-head__title">Comunicação</h1>
          <p className="adm-head__desc">
            Quanto o chat é usado: mensagens por dia, quem conversa, em que horário e quanto tempo uma conversa direta
            espera resposta.
          </p>
        </div>
      </div>

      <div className="cons-presets" role="tablist" aria-label="Período">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`cons-preset${preset === p.id ? ' cons-preset--on' : ''}`}
            aria-selected={preset === p.id}
            onClick={() => setPreset(p.id)}
          >
            {p.label}
          </button>
        ))}
        {preset === 'periodo' ? (
          <span className="cons-periodo">
            <DateInput value={ini} onChange={setIni} placeholder="Início" />
            <span className="cons-periodo__sep">até</span>
            <DateInput value={fim} onChange={setFim} placeholder="Fim" />
          </span>
        ) : null}
      </div>

      {preset === 'periodo' && (from === '' || to === '') ? (
        <p className="adm-hint">Escolha a data inicial e a final para ver o uso do período.</p>
      ) : q.isPending ? (
        <LoadingState variant="detail" label="Carregando uso da Comunicação" />
      ) : q.isError || !d ? (
        <p className="adm-hint">Não foi possível carregar o uso da Comunicação.</p>
      ) : vazio ? (
        <div className="cons-panel">
          <p className="adm-hint">Nenhuma mensagem enviada neste período.</p>
        </div>
      ) : (
        <>
          <div className="cons-cards">
            <Metric icon="fas fa-comment-dots" valor={d.totais.mensagens} label="Mensagens" sub="enviadas no período" />
            <Metric icon="fas fa-user-pen" valor={d.totais.pessoas} label="Pessoas ativas" sub="que enviaram ao menos uma" />
            <Metric icon="fas fa-hashtag" valor={d.totais.canais} label="Canais com movimento" sub="receberam mensagem" />
            <Metric icon="fas fa-plus-circle" valor={d.totais.novosCanais} label="Novas conversas" sub="canais criados no período" />
            <Metric icon="fas fa-paperclip" valor={d.totais.anexos} label="Anexos" sub="arquivos, enquetes e afins" />
            <Metric icon="fas fa-face-smile" valor={d.totais.reacoes} label="Reações" sub="em mensagens do período" />
          </div>

          <div className="cons-panel">
            <div className="cons-panel__head">
              <h2 className="cons-panel__title">Mensagens por dia</h2>
              <span className="cons-panel__meta">
                {nf.format(d.totais.mensagens)} mensagens · barra clara = pessoas que enviaram
              </span>
            </div>
            <div className="cons-daybars">
              {d.porDia.map((x) => (
                <span
                  className="cons-daybar"
                  key={x.dia}
                  title={`${diaCurto(x.dia)} · ${nf.format(x.mensagens)} mensagens · ${nf.format(x.pessoas)} pessoas`}
                >
                  <span className="cons-daybar__fill" style={{ height: `${(x.mensagens / maxDia) * 100}%` }}>
                    <span className="cons-daybar__val">{x.mensagens}</span>
                    <span className="cons-daybar__pessoas" style={{ height: `${(x.pessoas / Math.max(1, x.mensagens)) * 100}%` }} />
                  </span>
                  <span className="cons-daybar__lbl">{diaCurto(x.dia)}</span>
                </span>
              ))}
            </div>
          </div>

          <div className="cons-cols">
            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Hora do dia</h2>
                <span className="cons-panel__meta">horário de Brasília</span>
              </div>
              <div className="cons-hours">
                {horas.map((h) => (
                  <span className="cons-hour" key={h.hora} title={`${String(h.hora).padStart(2, '0')}h · ${nf.format(h.total)} mensagens`}>
                    <span className="cons-hour__fill" style={{ height: `${(h.total / maxHora) * 100}%` }} />
                    {h.hora % 3 === 0 ? <span className="cons-hour__lbl">{h.hora}h</span> : <span className="cons-hour__lbl" />}
                  </span>
                ))}
              </div>
            </div>

            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Dia da semana</h2>
              </div>
              <ul className="cons-feat">
                {semana.map((s) => (
                  <li className="cons-feat__row" key={s.dia}>
                    <span className="cons-feat__name">{DIA_SEMANA[s.dia - 1]}</span>
                    <span className="cons-bar">
                      <span className="cons-bar__fill" style={{ width: `${(s.total / maxSemana) * 100}%` }} />
                    </span>
                    <span className="cons-feat__val">{nf.format(s.total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="cons-cols">
            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Por tipo de canal</h2>
              </div>
              <ul className="cons-feat cons-feat--largo">
                {d.porTipo.map((t) => (
                  <li className="cons-feat__row" key={t.kind}>
                    <span className="cons-feat__name">
                      <i className={`${TIPO_CANAL_ICON[t.kind] ?? 'fas fa-hashtag'} cons-kind__ico`} aria-hidden />
                      {TIPO_CANAL_LABEL[t.kind] ?? t.kind}
                      <span className="cons-tree__badge">{t.canais} {t.canais === 1 ? 'canal' : 'canais'}</span>
                    </span>
                    <span className="cons-bar">
                      <span className="cons-bar__fill cons-bar__fill--alt" style={{ width: `${(t.mensagens / maxTipo) * 100}%` }} />
                    </span>
                    <span className="cons-feat__val">{nf.format(t.mensagens)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Resposta nas conversas diretas</h2>
              </div>
              <div className="cons-resp">
                <span className="cons-resp__valor">{minutosPorExtenso(d.respostaDireta.medianaMin)}</span>
                <span className="cons-resp__label">tempo mediano até a outra pessoa responder</span>
                <span className="cons-resp__sub">
                  {d.respostaDireta.amostras === 0
                    ? 'nenhuma troca de mensagens entre duas pessoas no período'
                    : `${nf.format(d.respostaDireta.amostras)} respostas medidas · metade chegou antes disso`}
                </span>
              </div>
              {d.anexos.length > 0 ? (
                <ul className="cons-kv">
                  {d.anexos.map((a) => (
                    <li className="cons-kv__row" key={a.kind}>
                      <span>{ANEXO_LABEL[a.kind] ?? a.kind}</span>
                      <span className="cons-kv__val">{nf.format(a.total)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="cons-cols">
            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Canais mais ativos</h2>
              </div>
              <ol className="cons-rank">
                {d.rankCanais.map((r, i) => (
                  <li className="cons-rank__row" key={r.channelId}>
                    <span className="cons-rank__pos">{i + 1}</span>
                    <span className="cons-rank__av cons-rank__av--canal">
                      <i className={TIPO_CANAL_ICON[r.kind] ?? 'fas fa-hashtag'} aria-hidden />
                    </span>
                    <span className="cons-rank__main">
                      <span className="cons-rank__name">
                        <span className="cons-rank__txt" title={r.name}>{r.name}</span>
                        <span className="cons-tree__badge">{TIPO_CANAL_LABEL[r.kind] ?? r.kind}</span>
                      </span>
                      <span className="cons-bar">
                        <span className="cons-bar__fill" style={{ width: `${(r.mensagens / maxCanal) * 100}%` }} />
                      </span>
                    </span>
                    <span className="cons-rank__val" title={`${r.pessoas} pessoas`}>{nf.format(r.mensagens)}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Quem mais conversa</h2>
              </div>
              <ol className="cons-rank">
                {d.rankPessoas.map((r, i) => (
                  <li className="cons-rank__row" key={r.memberId ?? i}>
                    <span className="cons-rank__pos">{i + 1}</span>
                    <span className="cons-rank__av">{iniciais(r.name)}</span>
                    <span className="cons-rank__main">
                      <span className="cons-rank__name">
                        <span className="cons-rank__txt" title={r.name}>{r.name}</span>
                        <span className="cons-tree__badge">{r.canais} {r.canais === 1 ? 'canal' : 'canais'}</span>
                      </span>
                      <span className="cons-bar">
                        <span className="cons-bar__fill" style={{ width: `${(r.mensagens / maxPessoa) * 100}%` }} />
                      </span>
                    </span>
                    <span className="cons-rank__val">{nf.format(r.mensagens)}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {d.porWorkspace.length > 1 ? (
            <div className="cons-panel">
              <div className="cons-panel__head">
                <h2 className="cons-panel__title">Por workspace</h2>
              </div>
              <div className="cons-table-wrap">
                <table className="cons-table">
                  <thead>
                    <tr>
                      <th>Workspace</th>
                      <th className="cons-table__num">Mensagens</th>
                      <th className="cons-table__num">Pessoas</th>
                      <th className="cons-table__num">Canais</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.porWorkspace.map((w) => (
                      <tr key={w.workspaceId}>
                        <td>
                          <span className="cons-ws">
                            <i className="fas fa-layer-group cons-tree__ico cons-tree__ico--ws" aria-hidden /> {w.name}
                          </span>
                        </td>
                        <td className="cons-table__num">{nf.format(w.mensagens)}</td>
                        <td className="cons-table__num">{nf.format(w.pessoas)}</td>
                        <td className="cons-table__num">{nf.format(w.canais)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}
